import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { GlobalStyles, StyledEngineProvider } from '@mui/material';

import { Config } from 'config';
import { ErrorBoundary } from 'components';

import App from './App';

const DEFAULT_FONT_SIZE = 17;
const DYNAMIC_TYPE_SCALE_PROPERTY = '--dynamic-type-scale';

const { sentry } = Config;
const isProductionTelemetry = sentry.env === 'production';

const isIOS = () => {
  const userAgent = navigator.userAgent;

  return (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1)
  );
};

const updateDynamicTypeScale = () => {
  if (
    !document.body ||
    !isIOS() ||
    !CSS.supports('font', '-apple-system-body')
  ) {
    return;
  }

  const probe = document.createElement('span');
  probe.textContent = 'M';
  probe.style.font = '-apple-system-body';
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  probe.style.pointerEvents = 'none';
  probe.style.whiteSpace = 'nowrap';

  document.body.appendChild(probe);

  const fontSize = Number.parseFloat(getComputedStyle(probe).fontSize);
  const scale = fontSize / DEFAULT_FONT_SIZE;

  if (Number.isFinite(scale) && scale > 0) {
    document.documentElement.style.setProperty(
      DYNAMIC_TYPE_SCALE_PROPERTY,
      scale.toFixed(4)
    );
  }

  probe.remove();
};

updateDynamicTypeScale();
// SPA-level listeners intentionally live for the full app lifecycle.
window.addEventListener('pageshow', updateDynamicTypeScale);
document.addEventListener('visibilitychange', () => {
  // iOS settings changes are only observable once the page becomes active again.
  if (document.visibilityState === 'visible') {
    updateDynamicTypeScale();
  }
});

if (sentry.dsn) {
  if (sentry.release) {
    console.log(`VERSION ${sentry.release}`);
  }
  try {
    Sentry.init({
      dsn: sentry.dsn,
      enableLogs: true,
      environment: sentry.env,
      release: sentry.release,
      tracesSampleRate: isProductionTelemetry ? 0 : 1.0,
      replaysSessionSampleRate: isProductionTelemetry ? 0 : 0.1,
      replaysOnErrorSampleRate: isProductionTelemetry ? 0 : 1.0,
      ignoreErrors: [
        'AxiosError',
        'TimeoutError',
        'Loading chunk',
        "Unexpected token '<'",
        'Failed to fetch dynamically',
      ],
      integrations: isProductionTelemetry
        ? []
        : [
            Sentry.replayIntegration(),
            Sentry.browserTracingIntegration(),
            Sentry.consoleLoggingIntegration(),
          ],
    });
    Sentry.setUser(null);
  } catch (error) {
    console.error(error);
  }
}

if (Config.waf.apiUrl) {
  const AwsWafScript = document.createElement('script');
  AwsWafScript.id = 'AwsWAFScript';
  AwsWafScript.async = false;
  AwsWafScript.src = Config.waf.apiUrl;
  document.head.appendChild(AwsWafScript);
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <StyledEngineProvider enableCssLayer>
    <GlobalStyles styles="@layer theme, base, mui, components, utilities;" />
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StyledEngineProvider>
);
